/**
 * Runtime SEO environment detection (hostname-based).
 *
 * Used to force noindex and suppress production canonical/JSON-LD on non-production hosts.
 * Safe when `window` is unavailable (SSR/prerender server context): all checks return false.
 */

const STAGING_HOST = 'testing.yovotrip.com';

const PRODUCTION_HOSTS = new Set(['yovotrip.com', 'www.yovotrip.com']);

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

function getHostname() {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.hostname || '';
}

/** Public staging / QA frontend host — must never be indexed. */
export function isStagingHost() {
  return getHostname() === STAGING_HOST;
}

/** Production marketing hosts — existing SEO behavior applies. */
export function isProductionHost() {
  return PRODUCTION_HOSTS.has(getHostname());
}

/** Local development — must not be indexed. */
export function isLocalHost() {
  return LOCAL_HOSTS.has(getHostname());
}

/**
 * When true (CRA build-time), treat runtime as production SEO even on 127.0.0.1.
 * Needed for `npm run build:seo` on VMs: Playwright prerenders via localhost, but
 * output HTML must keep production canonical / index,follow for Azure Blob/$web.
 * Do NOT set on testing.yovotrip.com or local `build:staging`.
 */
function isPrerenderAsProduction() {
  return process.env.REACT_APP_SEO_PRERENDER_AS_PRODUCTION === 'true';
}

/**
 * Non-production SEO hosts: force noindex and omit production canonical / JSON-LD.
 * Staging and localhost only; does not affect www.yovotrip.com or yovotrip.com.
 */
export function isNonProductionSeoHost() {
  if (isPrerenderAsProduction()) {
    return false;
  }
  return isStagingHost() || isLocalHost();
}
